import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import MDSpinner from 'react-md-spinner';
import momentZ from 'moment-timezone';
import {createRequest} from '../../utils/HttpRequestUtills';
import {CopyToBufferButton} from '../general/CopyToBufferButton';

// A/B links: one ad link that splits visitors between 2-5 funnel variants (backend: ab-links-serverless.yml).

const APPS = [{value: 'ReelsMaker', label: 'BEAT'}, {value: 'AiEditor', label: 'Astify'}];
const MAX_VARIANTS = 5;
const STATUS_LABELS = {draft: 'Draft', active: 'Running', stopped: 'Stopped'};

const request = (dispatch, method, path, body) => new Promise((resolve, reject) => {
  const xhr = createRequest(dispatch, method, path, (text) => resolve(JSON.parse(text || '{}')), true, (text) => {
    try {
      reject(new Error(JSON.parse(text).description || text));
    } catch (e) {
      reject(new Error(text || 'Request failed'));
    }
  });
  xhr.send(body ? JSON.stringify(body) : undefined);
});

const appLabel = (appId) => (APPS.find((app) => app.value === appId) || {}).label || appId;
const usd = (v) => `$${Number(v || 0).toLocaleString('en-US', {maximumFractionDigits: 2})}`;
const pct = (part, total) => (total ? `${(100 * part / total).toFixed(1)}%` : '—');

const NewTestForm = ({dispatch, onCreated, onCancel}) => {
  const [appId, setAppId] = useState('ReelsMaker');
  const [name, setName] = useState('');
  const [variants, setVariants] = useState([{url: '', label: '', weight: 1}, {url: '', label: '', weight: 1}]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const setVariant = (index, patch) => setVariants(variants.map((v, i) => (i === index ? {...v, ...patch} : v)));
  const submit = () => {
    setSaving(true);
    setError('');
    request(dispatch, 'POST', '/v1/ab-links', {appId, name, variants: variants.map((v) => ({...v, weight: Number(v.weight) || 1}))})
        .then((result) => onCreated(result.test))
        .catch((e) => setError(e.message))
        .finally(() => setSaving(false));
  };

  return <div className={'ab-links-card'}>
    <div className={'ab-links-row'}>
      <label>App<select className={'onelight-input'} value={appId} onChange={(e) => setAppId(e.target.value)}>
        {APPS.map((app) => <option key={app.value} value={app.value}>{app.label}</option>)}
      </select></label>
      <label className={'ab-links-grow'}>Name<input className={'onelight-input'} value={name} placeholder={'Travel vs no-upload, US'} onChange={(e) => setName(e.target.value)}/></label>
    </div>
    {variants.map((variant, index) => <div key={index} className={'ab-links-row'}>
      <label className={'ab-links-label'}>Variant<input className={'onelight-input'} value={variant.label} placeholder={String.fromCharCode(65 + index)}
        onChange={(e) => setVariant(index, {label: e.target.value})}/></label>
      <label className={'ab-links-grow'}>Funnel URL<input className={'onelight-input'} value={variant.url} placeholder={'https://reels-maker.onelightapps.io/hcvhf7pz/travel'}
        onChange={(e) => setVariant(index, {url: e.target.value})}/></label>
      <label className={'ab-links-weight'}>Weight<input className={'onelight-input'} type={'number'} min={1} max={100} value={variant.weight}
        onChange={(e) => setVariant(index, {weight: e.target.value})}/></label>
      {variants.length > 2 && <button className={'onelight-second-btn ab-links-link'} onClick={() => setVariants(variants.filter((v, i) => i !== index))}>Remove</button>}
    </div>)}
    {variants.length < MAX_VARIANTS &&
      <button className={'onelight-second-btn ab-links-link'} onClick={() => setVariants([...variants, {url: '', label: '', weight: 1}])}>+ Add variant</button>}
    {error && <div className={'ab-links-error'}>{error}</div>}
    <div className={'ab-links-row'}>
      <button className={'onelight-btn-apply ab-links-apply'} disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Create (draft)'}</button>
      <button className={'onelight-second-btn ab-links-link'} onClick={onCancel}>Cancel</button>
    </div>
  </div>;
};

const TestsList = ({dispatch}) => {
  const history = useHistory();
  const [tests, setTests] = useState(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    request(dispatch, 'GET', '/v1/ab-links').then((r) => setTests(r.tests || [])).catch((e) => setError(e.message));
  }, [dispatch]);

  return <div className={'ab-links-container'}>
    <div className={'ab-links-header'}>
      <div className={'aso-report-item-header'}>A/B links</div>
      {!creating && <button className={'onelight-btn-apply ab-links-apply'} onClick={() => setCreating(true)}>New test</button>}
    </div>
    <div className={'ab-links-muted'}>One link for an ad campaign: visitors are split between the funnel variants and keep their
      variant on return. All ad parameters (fbclid, gclid, utm_*) are passed to the funnel unchanged.</div>
    {creating && <NewTestForm dispatch={dispatch} onCancel={() => setCreating(false)} onCreated={(test) => history.push(`/ab-links/${test.id}`)}/>}
    {error && <div className={'ab-links-error'}>{error}</div>}
    {!tests && !error && <div className={'spinner-container'} style={{marginTop: '150px'}}><MDSpinner/></div>}
    {tests && <div className={'ab-links-card'}><table className={'ab-links-table'}>
      <thead><tr><th>Name</th><th>App</th><th>Status</th><th>Variants</th><th>Visits</th><th>Created</th></tr></thead>
      <tbody>{tests.map((test) => <tr key={test.id} onClick={() => history.push(`/ab-links/${test.id}`)}>
        <td>{test.name}</td><td>{appLabel(test.appId)}</td>
        <td><span className={`ab-links-status ab-links-status--${test.status}`}>{STATUS_LABELS[test.status] || test.status}</span></td>
        <td>{test.variantsCount}</td><td>{test.visits}</td><td>{momentZ(test.createdAt).format('YYYY-MM-DD')}</td>
      </tr>)}</tbody>
    </table></div>}
    {tests && tests.length === 0 && <div className={'ab-links-muted'}>No tests yet.</div>}
  </div>;
};

const TestDetails = ({dispatch}) => {
  const {id} = useParams();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(() => {
    const query = [from && `from=${from}`, to && `to=${to}`].filter(Boolean).join('&');
    request(dispatch, 'GET', `/v1/ab-links/${id}${query ? `?${query}` : ''}`).then(setData).catch((e) => setError(e.message));
  }, [dispatch, id, from, to]);
  useEffect(load, [load]);

  const setStatus = (status) => {
    const question = status === 'active' ? 'Start the test? Its variants cannot change after that.' :
      'Stop the test? The link keeps working and sends everyone to the first variant.';
    if (!window.confirm(question)) {
      return;
    }
    request(dispatch, 'PUT', `/v1/ab-links/${id}`, {status}).then(setData).catch((e) => setError(e.message));
  };

  if (!data) {
    return <div className={'ab-links-container'}>{error ? <div className={'ab-links-error'}>{error}</div> : <div className={'spinner-container'} style={{marginTop: '150px'}}><MDSpinner/></div>}</div>;
  }
  const {test, variants} = data;
  const totalVisitors = variants.reduce((sum, v) => sum + v.stats.visitors, 0);
  return <div className={'ab-links-container'}>
    <button className={'onelight-second-btn ab-links-link'} onClick={() => history.push('/ab-links')}>← All tests</button>
    <div className={'ab-links-header'}>
      <div className={'aso-report-item-header'}>{test.name}</div>
      <span className={`ab-links-status ab-links-status--${test.status}`}>{STATUS_LABELS[test.status] || test.status}</span>
      {test.status === 'draft' && <button className={'onelight-btn-apply ab-links-apply'} onClick={() => setStatus('active')}>Start</button>}
      {test.status === 'active' && <button className={'onelight-btn-apply ab-links-apply ab-links-apply--danger'} onClick={() => setStatus('stopped')}>Stop</button>}
    </div>
    <div className={'ab-links-row'}>
      <code className={'ab-links-url'}>{test.url}</code>
      <CopyToBufferButton value={test.url} style={{width: 18, height: 18, cursor: 'pointer'}}/>
      <span className={'ab-links-muted'}>{appLabel(test.appId)} · created {momentZ(test.createdAt).format('YYYY-MM-DD')}
        {test.startedAt && ` · started ${momentZ(test.startedAt).format('YYYY-MM-DD HH:mm')}`}
        {test.stoppedAt && ` · stopped ${momentZ(test.stoppedAt).format('YYYY-MM-DD HH:mm')}`}</span>
    </div>
    {test.status === 'draft' && <div className={'ab-links-muted'}>Draft: the link answers 404 until the test is started.</div>}
    <div className={'ab-links-row'}>
      <label>From<input className={'onelight-input'} type={'date'} value={from} onChange={(e) => setFrom(e.target.value)}/></label>
      <label>To<input className={'onelight-input'} type={'date'} value={to} onChange={(e) => setTo(e.target.value)}/></label>
      <span className={'ab-links-muted'}>{momentZ(data.from).format('YYYY-MM-DD')} — {momentZ(data.to).format('YYYY-MM-DD')}</span>
    </div>
    {error && <div className={'ab-links-error'}>{error}</div>}
    <div className={'ab-links-card'}><table className={'ab-links-table'}>
      <thead><tr>
        <th>Variant</th><th>Weight</th><th>Visitors</th><th>Share</th><th>Visits</th><th>Sign-ups</th><th>Visit → sign-up</th>
        <th>Payers</th><th>Sign-up → payer</th><th>Revenue</th><th>Revenue / visitor</th>
      </tr></thead>
      <tbody>{variants.map((variant) => {
        const s = variant.stats;
        return <tr key={variant.id}>
          <td><b>{variant.label}</b><div className={'ab-links-muted ab-links-ellipsis'}>
            <a href={variant.url} target={'_blank'} rel={'noreferrer'}>{variant.url}</a></div></td>
          <td>{variant.weight}</td><td>{s.visitors}</td><td>{pct(s.visitors, totalVisitors)}</td><td>{s.visits}</td>
          <td>{s.registrations}{s.approximateRegistrations ? <span className={'ab-links-muted'}> ({s.approximateRegistrations} by IP)</span> : null}</td>
          <td>{pct(s.registrations, s.visitors)}</td><td>{s.payers}</td><td>{pct(s.payers, s.registrations)}</td>
          <td>{usd(s.revenueUsd)}</td><td>{s.visitors ? usd(s.revenueUsd / s.visitors) : '—'}</td>
        </tr>;
      })}</tbody>
    </table></div>
    <div className={'ab-links-muted'}>Visitors — first visits (returns with the cookie count only as visits). Sign-ups are tied to a visit by the
      ad click id (fbclid / gclid); visits without one are matched by IP and browser within 30 minutes. Revenue — paid orders in USD.</div>
  </div>;
};

export const AbLinks = connect()(TestsList);
export const AbLinkTest = connect()(TestDetails);
export default AbLinks;
